import { AfterContentChecked, Component, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ColumnMode, DatatableComponent } from '@siemens/ngx-datatable';
import { Observable, combineLatest, catchError, EMPTY, finalize } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { Page } from '../../../../core/components/table-footer-paging/table-footer-paging.component';
import { BxLogger } from '../../../../core/services/bx-logger.service';
import { ColumnWidthsService } from '../../../../core/services/column-widths.service';
import { DialogService } from '../../../../core/services/dialog.service';
import { InvoiceSharingService } from '../../../../core/services/invoice-sharing.service';
import { LocalStorageService } from '../../../../core/services/local-storage.service';
import { FeatureService } from '../../../../core/services/permission';
import { PermissionService } from '../../../../core/services/permission';
import { StoresService } from '../../../../core/services/stores';
import { TablesHelperService } from '../../../../core/services/tables-helper.service';
import { ExportFormat, ExportInvoicesOptions, Job, Tables, TaxCalculationMethod } from '../../../../data/models';
import { RepoExportService } from '../../../../data/repositories/repo-export.service';
import { RepoJobsService } from '../../../../data/repositories/repo-jobs.service';
import { RepoSettingsService } from '../../../../data/repositories/repo-settings.service';
import { SharedDataService } from '../../../../data/services/shared-data.service';
import { ReportService } from '../../../../reports/report.service';
import { CustomLayoutsService } from '../../../estimates/custom-layouts/custom-layouts.service';
import { LayoutService } from '../../../layout/layout.service';
import { InvoicesReceiptService } from '../invoices-receipt.service';
import { InvoicesService } from '../invoices.service';
import { JobPayment } from '../../../../data/models';
import { InvoicesApiService } from '../domain/apis/invoices-api.service';
import { InvoiceListModel } from '../domain/entities/invoice-list.model';
import { JobPaymentFilterInput, JobPaymentSortInput, Uuid } from '../../../../../graphql';
import { SortEvent } from '../../../../data/models/datatable.model';

type TableColWidths = {
    invoiceNumber: number;
    description: number;
    invoiceDate: number;
    dueDate: number;
    totalIncTax: number;
    status: number;
    jobClientName: number;
    jobDescription: number;
} & Record<string | number, number>;

type SortFields = {
    [key: string]: JobPaymentSortInput;
};

@UntilDestroy()
@Component({
    selector: 'app-invoices2',
    templateUrl: './invoices2.component.html',
    standalone: false,
})
export class Invoices2Component implements OnInit, AfterContentChecked {
    #invoicesApiService = inject(InvoicesApiService);

    title = 'All Invoices';
    rows: InvoiceListModel[] = [];
    selected: InvoiceListModel[] = [];
    isLoading = false;
    page = new Page();
    search?: string;
    currentComponentWidth?: number;
    sortField?: string = 'invoiceDate';
    sortDirection: 'ASC' | 'DESC' = 'DESC';
    taxDisplayStatus = false;
    selectedValue = 0;
    invoicesWidths = {} as TableColWidths;
    isSendInvoiceEmailVisible = true;

    // url params
    status: string | undefined;

    settingsActions = [
        {
            groupName: 'Display Total',
            actions: [
                { label: 'Excluding Tax', condition: true, value: 0 },
                { label: 'Including Tax', condition: true, value: 1 },
            ],
        },
    ];

    simpleItems = [
        {
            actions: [
                { label: 'Manage Payment Terms', condition: true, value: 0 },
                { label: 'Reset View to Default', condition: true, value: 1 },
            ],
        },
    ];

    moreActions = [
        {
            value: 0,
            label: 'Export to Excel',
            condition: true,
        },
    ];

    // @ts-ignore TS2564
    @ViewChild('myTable') table: DatatableComponent;
    // @ts-ignore TS7008
    @ViewChild('tableWrapper') tableWrapper;

    // @ts-ignore TS2564
    public hasPermission$: Observable<boolean>;

    #usingAdjustments: boolean;

    constructor(
        private logger: BxLogger,
        private settings: RepoSettingsService,
        private invoiceSharingService: InvoiceSharingService,
        private customLayoutsService: CustomLayoutsService,
        private jobs: RepoJobsService,
        public permissionService: PermissionService,
        private reportService: ReportService,
        private featureService: FeatureService,
        public layoutService: LayoutService,
        private localStorageService: LocalStorageService,
        private columnWidthsService: ColumnWidthsService,
        private repoExportService: RepoExportService,
        private invoicesService: InvoicesService,
        private invoicesReceiptService: InvoicesReceiptService,
        private tablesHelperService: TablesHelperService,
        private storesService: StoresService,
        private dialogService: DialogService,
        private sharedData: SharedDataService,
        private route: ActivatedRoute
    ) {
        this.#usingAdjustments =
            this.sharedData.UserData.tenant.BillingCountry === 'New Zealand' ||
            this.sharedData.UserData.tenant.BillingCountry === 'Australia';
    }

    ngOnInit() {
        this.hasPermission$ = this.hasPermission();

        if (this.localStorageService.getObject('invoices-table')) {
            this.invoicesWidths = this.localStorageService.getObject('invoices-table');
        }

        this.page = new Page();
        this.setDefaults();
        this.load();

        this.isSendInvoiceEmailVisible = this.featureService.isStateAvailable('jobs.maintain-invoices.email-invoice');
        this.storesService.updateInvoiceStatus$.pipe(untilDestroyed(this)).subscribe((inv) => {
            this.updateInvoiceRow(inv);
        });
    }

    ngAfterContentChecked() {
        if (this.table && this.tableWrapper.nativeElement.clientWidth !== this.currentComponentWidth) {
            this.currentComponentWidth = this.tableWrapper.nativeElement.clientWidth;
            this.rows = [...this.rows];
        }
    }

    private hasPermission(): Observable<boolean> {
        return combineLatest([
            this.permissionService.getState('jobs.maintain-invoices.view-invoice-details'),
            this.featureService.getState('jobs.view.invoices'),
        ]).pipe(map(([hasPermission, hasFeature]) => hasPermission && hasFeature));
    }

    setDefaults() {
        this.page.pageNumber = 0;
        this.page.size = this.settings.AllInvoicesViewPageCount;
    }

    // @ts-ignore TS7006
    pageLimit(event) {
        this.page.size = Number.parseInt(event.target.textContent);
        this.page.pageNumber = 0;
        this.settings.AllInvoicesViewPageCount = this.page.size;
        this.loadPage();
    }

    load() {
        this.status = this.route.snapshot.queryParams.status;
        this.title = 'All Invoices';
        this.loadPage();
    }

    loadPage() {
        this.isLoading = true;
        const take = this.page.size;
        const skip = this.page.pageNumber * this.page.size;
        let sort: JobPaymentSortInput = {};
        if (this.sortField) {
            switch (this.sortField) {
                case 'invoiceNumber':
                    sort = { invoiceNumber: this.sortDirection };
                    break;
                case 'description':
                    sort = { description: this.sortDirection };
                    break;
                case 'jobDescription':
                    sort = { job: { description: this.sortDirection } };
                    break;
                case 'jobClientName':
                    sort = { job: { clientName: this.sortDirection } };
                    break;
                case 'invoiceDate':
                    sort = { invoiceDate: this.sortDirection };
                    break;
                case 'dueDate':
                    sort = { dueDate: this.sortDirection };
                    break;
                case 'totalIncTax':
                    sort = { totalIncTax: this.sortDirection };
                    break;
                case 'status':
                    sort = { status: this.sortDirection };
                    break;
            }
        }

        let where: JobPaymentFilterInput = { and: [{ isDeleted: { eq: false } }] };

        this.#invoicesApiService
            .getJobInvoices(take, skip, this.search, this.status, [sort], where)
            .pipe(
                first(),
                catchError((err) => {
                    this.logger.error('An error occurred loading job invoices.');
                    return EMPTY;
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe((result) => {
                if (result.data.jobPayments) {
                    this.page.totalElements = result.data.jobPayments.totalCount;
                    this.rows = [...result.data.jobPayments.items!];
                    console.log('loaded');
                    // setTimeout(() => {
                    //     this.table.columnMode = ColumnMode.force;
                    // }, 2000);
                }
            });
    }

    reloadInvoice(invoice: InvoiceListModel) {
        const take = 1;
        const skip = 0;
        const where: JobPaymentFilterInput = {
            and: [{ iD_JobPayment: { eq: invoice.iD_JobPayment } }, { iD_Job: { eq: invoice.iD_Job } }],
        };
        this.#invoicesApiService
            .getJobInvoices(take, skip, null, null, null, where)
            .pipe(first())
            .subscribe((result) => {
                if (result.data?.jobPayments?.items?.length) {
                    const refreshedRow = result.data.jobPayments.items[0]!;
                    this.rows = this.rows.map((r) => (r.iD_JobPayment === invoice.iD_JobPayment ? refreshedRow : r));
                } else {
                    // it's been deleted
                    this.rows = this.rows.filter((r) => r.iD_JobPayment !== invoice.iD_JobPayment);
                }
            });
    }

    setSearch(event: string | undefined) {
        this.search = event;
        this.page.pageNumber = 0;
        this.load();
    }

    setPage(pageInfo: { offset: number }) {
        if (this.page.pageNumber !== pageInfo.offset) {
            this.page.pageNumber = pageInfo.offset;
            this.load();
        }
    }

    onSort(event: SortEvent<any>) {
        if (event.sorts.length) {
            const newSortField = event.sorts[0].prop.toString();
            const oldSortField = this.sortField;
            this.sortField = event.sorts[0].prop.toString();
            // For datatable, when switching to a different column for sorting,
            // it always defaults to DESC
            if (newSortField !== oldSortField) {
                this.sortDirection = 'DESC';
            } else {
                this.sortDirection = event.sorts[0].dir.toUpperCase() as 'ASC' | 'DESC';
            }
            this.loadPage();
        }
    }

    async viewInvoice(invoice: InvoiceListModel) {
        try {
            await this.invoicesService.viewInvoiceById(invoice.iD_JobPayment, invoice.iD_Job);
            // In case the invoice status is changed
            this.reloadInvoice(invoice);
        } catch (_) {
            // cancelled
        }
    }

    updateInvoiceRow(invoice: JobPayment) {
        const row = this.rows.find((r) => {
            return r.invoiceNumber === invoice.InvoiceNumber;
        });
        if (row) {
            row.status = invoice.Status;
        }
    }

    async addReceipt(invoice: InvoiceListModel) {
        try {
            await this.invoicesReceiptService.addReceiptByInvoiceId(invoice.iD_JobPayment, invoice.iD_Job);
            await this.jobs.Save(null, 'Receipt saved');
            this.reloadInvoice(invoice);
        } catch (_) {
            // cancelled
        }
    }

    printInvoice(invoice: InvoiceListModel) {
        let fileName = invoice.job.number ? invoice.job.number + '-' : '';
        fileName += 'Invoice-' + invoice.invoiceNumberString;

        const reportName = this.#usingAdjustments ? 'jobpaymentv2' : 'jobpayment';
        const params = {
            reportName,
            reportTitle: 'Invoice Report',
            params: {
                ID_JobPayment: invoice.iD_JobPayment,
                Draft: invoice.status === 'Pending',
                ID_Job: invoice.iD_Job,
                ID_Estimate: invoice.job.iD_Estimate,
                CalculationMethod: 0,
            },
            fileName: fileName,
        };
        switch (invoice.job.calculationMethod) {
            case 'LEGACY':
                params.params.CalculationMethod = TaxCalculationMethod.Legacy;
                break;
            case 'MULTITAX':
                params.params.CalculationMethod = TaxCalculationMethod.Multitax;
                break;
            case 'MULTITAXRECIPES':
                params.params.CalculationMethod = TaxCalculationMethod.Multitaxrecipes;
                break;
        }
        this.reportService.generate(params);
    }

    async emailInvoice(invoice: InvoiceListModel) {
        const documentSource = { Id: invoice.iD_Job, Type: 'Job' };
        const breezeJob = (await this.jobs.Get(invoice.iD_Job)) as unknown as Job;
        const breezeJobPayment = breezeJob.JobPayments?.find((jp) => jp.ID_JobPayment == invoice.iD_JobPayment);
        if (breezeJobPayment) {
            const emailConfig = await this.invoiceSharingService.getEmailConfig(breezeJobPayment, 'pdf');
            const shareConfig = this.invoiceSharingService.getShareConfig(breezeJobPayment);
            this.customLayoutsService.openDigitalAcceptanceShareForm(
                emailConfig,
                shareConfig,
                null,
                true,
                3,
                false,
                breezeJobPayment.ID_Job,
                documentSource,
                true,
                breezeJobPayment,
                () => {
                    this.logger.success('Invoice Shared');
                    this.reloadInvoice(invoice);
                }
            ); // 3 : DocumentTypes.Invoice
        }
    }

    termsAndConditions() {
        this.invoicesService.viewTermsAndConditions();
    }

    async deleteInvoice(evt: MouseEvent, invoice: InvoiceListModel) {
        evt.stopPropagation();
        const params = {
            title: 'Delete invoice?',
            text: "If you go ahead, you won't be able to get it back.",
        };

        try {
            await this.dialogService.show(params);
            // Use the existing breeze method because of breeze event handlers and
            // job total recalculations that fire when IsDeleted is set to true;
            const breezeJob = (await this.jobs.Get(invoice.iD_Job)) as unknown as Job;
            const breezeJobPayment = breezeJob.JobPayments?.find((jp) => jp.ID_JobPayment === invoice.iD_JobPayment);
            if (breezeJobPayment) {
                breezeJobPayment.IsDeleted = true;
                await this.jobs.Save(null, 'Invoice has been deleted');
                this.reloadInvoice(invoice);
            }
        } catch (_) {
            // cancelled
        }
    }

    // @ts-ignore TS7006
    resolveSettingsAction(value) {
        switch (value) {
            case 0: {
                this.taxDisplayStatus = false;
                break;
            }
            case 1: {
                this.taxDisplayStatus = true;
                break;
            }
        }
    }

    resetColumnWidths() {
        this.columnWidthsService.resetTableUsingTableName(Tables.Invoices);
    }

    // @ts-ignore TS7006
    selectItem(val) {
        this.selectedValue = val;
        this.resolveSettingsAction(val);
    }

    // @ts-ignore TS7006
    resolveSimpleItemsSettingsAction(value) {
        switch (value) {
            case 0: {
                this.termsAndConditions();
                break;
            }
            case 1: {
                this.isLoading = true;
                this.invoicesWidths = {} as TableColWidths;
                this.resetColumnWidths();
                const tempRows = [...this.rows];
                this.rows.length = 0;
                setTimeout(() => {
                    this.rows = [...tempRows];
                    this.isLoading = false;
                }, 100);
                break;
            }
        }
    }

    onResize() {
        const invoicesColWidths = {} as TableColWidths;
        this.table._internalColumns.forEach((row) => {
            const colKey = row.prop;
            const colWidth = row.width;
            if (colKey && colWidth) invoicesColWidths[colKey] = colWidth;
        });
        this.localStorageService.saveObject('invoices-table', invoicesColWidths);
    }

    exportListToExcel() {
        const options: ExportInvoicesOptions = {
            format: ExportFormat.XLSX,
            filter: this.search,
            status: this.status ? this.status.split('|') : undefined,
        };
        this.repoExportService.exportInvoices(options);
    }

    resolveAction(action: number) {
        switch (action) {
            case 0: {
                this.exportListToExcel();
                break;
            }
        }
    }

    private resetPageNumber() {
        if (this.tablesHelperService.isResetPageNumber(this.page.totalElements - 1, this.page.size, this.page.pageNumber))
            this.page.pageNumber--;
    }
}
